import {
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import type { Queue } from 'lib-shared-types-api';
import { useState } from 'react';

import { facebookMessengerApi } from 'apis/facebookMessenger';
import { InputQueuesSelect } from 'components/inputQueuesSelect';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { InstagramFeatures } from '../../../instagramFeatures/instagramFeatures';

import styles from './instagramConnectModal.module.scss';

export const InstagramConnectModal = createAppModal(
  { type: 'settings:instagramConnect' },
  ({ isOpen, modal, hide, meta }): JSX.Element => {
    const notify = useNotifications();
    const [updateFacebookConnect, { isLoading }] =
      facebookMessengerApi.useUpdateFacebookConnectMutation();

    const { appId, facebookAuthenticationData } = modal.arg;

    const [selectedQueues, setSelectedQueues] = useState<Queue[]>([]);
    const queueId: string | undefined = selectedQueues[0]?.id;

    const [features, setFeatures] = useState<InstagramFeatures>({
      isStoryCommentsEnabled: true,
      isStoryMentionsEnabled: true,
    });

    const organizationId = useAppSelector(
      (state) => state.settings.organization.organization.id,
    );

    const handleClose = () => {
      setSelectedQueues([]);
      hide?.();
    };

    const handleConfirm = async () => {
      try {
        await updateFacebookConnect({
          default_queue_id: queueId,
          organization_id: organizationId,
          application_id: appId,
          user_id: facebookAuthenticationData?.userId,
          user_access_token: facebookAuthenticationData?.accessToken,
          connect_facebook: false,
          connect_instagram: true,
          instagram_messaging_settings: {
            story_comments_enabled: features.isStoryCommentsEnabled ?? true,
            story_mentions_enabled: features.isStoryMentionsEnabled ?? true,
          },
        }).unwrap();

        notify(
          {
            title: 'Instagram account successfully connected.',
            time: 3000,
          },
          NotificationType.Success,
        );

        meta?.resolve({ queueId });

        handleClose();
      } catch {
        notify(
          {
            title: 'An error occurred while connecting the Instagram account.',
          },
          NotificationType.Error,
        );
      }
    };

    return (
      <Modal isOpen={isOpen} onClose={handleClose} className={styles.modal}>
        <ModalHead title="Instagram for Business" onClose={handleClose} />

        <ModalBody>
          <Text.Paragraph subdued withMarginBottom>
            All the Instagram conversations will be sent to the selected queue.
          </Text.Paragraph>

          <FormGrid>
            <FormField>
              <InputQueuesSelect
                value={selectedQueues}
                onChange={({ selectedValues }) =>
                  setSelectedQueues(selectedValues)
                }
              />
            </FormField>

            <FormField>
              <InstagramFeatures onChange={setFeatures} />
            </FormField>
          </FormGrid>
        </ModalBody>

        <ModalFoot
          confirmName="Continue"
          confirmOnClick={handleConfirm}
          confirmProps={{
            disabled: queueId === undefined,
            isLoading,
          }}
          cancelOnClick={handleClose}
        />
      </Modal>
    );
  },
);
